// LA FICHE D'UNE PERSONNE de l'annuaire, en pleine page.
//
// Tout ce qu'elle a vécu avec l'équipe : ses compteurs (interactions, études,
// séances, verbatims), son agenda (les rendez-vous à venir), son historique
// (invitée par qui, a réservé, a modifié, séance réalisée avec qui…), les
// études qui l'ont concernée et ses invitations. On l'invite d'ici, on modifie
// sa fiche d'ici.
import { useState } from 'react';
import { useStudies } from '../api/queries';
import { googleCalendarLink } from '../calendar/api';
import { Button, Chip, chipState, EmptyState, ErrorBanner, Eyebrow, Select, Spinner } from '../components/ui';
import { BOOKING_LABELS, PROVIDER_LABELS, type BookingStatus } from '../invitations/api';
import { seniorityLabel } from '../personas/PersonaNeeds';
import { Link, useRouter } from '../router';
import { studyKind } from '../studies/kinds';
import { EditAction } from '../write/Forms';
import { engagementLabel, OVER_SOLICITED, personName, presetPeople, usePeople, usePersonDetail, type TimelineEvent } from './api';
import { FunBadges, PersonAvatar, SeniorityStars, SolicitationChip } from './PersonBadges';
import { PersonForm } from './PersonForm';
import './people.css';

const ICONS: Record<TimelineEvent['kind'], string> = {
  invite: '✉️', accept: '✅', decline: '🚫', cancel: '↩️', reschedule: '🔁', done: '🎙️', no_show: '⏳', verbatims: '💬',
};

const when = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const day = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

export function PersonPage({ projectId, personId }: { projectId: string; personId: string }) {
  const q = usePersonDetail(personId);
  const people = usePeople(projectId);
  const studies = useStudies(projectId);
  const { navigate } = useRouter();
  const [studyId, setStudyId] = useState('');
  const back = `/projet/${encodeURIComponent(projectId)}/personas`;
  const studyPath = (id: string) => `/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(id)}`;

  if (q.isPending) return <main className="screen"><Spinner label="Chargement de la fiche" /></main>;
  if (q.isError || !q.data) {
    return (
      <main className="screen">
        <Link to={back} className="zy-retour">← Annuaire</Link>
        <ErrorBanner>Personne introuvable dans votre périmètre.</ErrorBanner>
      </main>
    );
  }
  const d = q.data;
  const p = d.person;
  // La ligne de l'annuaire porte « déjà invitée » et le nombre d'invitations récentes.
  const row = people.data?.people.find((x) => x.id === p.id) ?? null;
  const withSessions = (studies.data ?? []).filter((s) => {
    const k = studyKind(s.type);
    return k !== 'audit' && k !== 'questionnaire' && !s.is_baseline;
  });
  const facts: [string, string | null][] = [
    ['E-mail', p.email], ['Téléphone', p.phone], ['Fonction', p.job_title], ['Rôle', p.role],
    ['Séniorité', seniorityLabel(p.seniority)], ['Engagement', engagementLabel(p.engagement)],
    ['Groupe', p.pool], ['Persona incarné', d.persona?.name ?? null], ['Notes', p.notes],
  ];

  return (
    <main className="screen ppl-page">
      <Link to={back} className="zy-retour">← Annuaire</Link>

      <div className="ppl-hero">
        <PersonAvatar person={p} />
        <div className="ppl-hero-main">
          <Eyebrow>{p.pool ?? 'Annuaire'}</Eyebrow>
          <h1 className="t-h1 ppl-hero-name">{personName(p)} <FunBadges person={p} /></h1>
          <p className="ppl-sub">{[p.job_title, p.role].filter(Boolean).join(' · ') || 'Fonction non renseignée'}</p>
          <span className="chips">
            <SeniorityStars value={p.seniority} />
            {p.key_user ? <Chip hue="cyan">🌟 Key user</Chip> : null}
            {p.domain_expert ? <Chip hue="violet">💪 Expert métier</Chip> : null}
            {row ? <SolicitationChip person={row} overSolicited={OVER_SOLICITED} /> : null}
            {p.do_not_contact ? <Chip state="erreur">Ne plus solliciter</Chip> : null}
          </span>
        </div>
        {d.canWrite ? (
          <div className="ppl-hero-actions">
            {!p.do_not_contact ? (
              <span className="ppl-invite">
                <Select aria-label="Étude" value={studyId} onChange={(e) => setStudyId(e.target.value)}>
                  <option value="">Inviter dans l’étude…</option>
                  {withSessions.map((s) => <option key={s.id} value={s.id}>{[s.code, s.objective].filter(Boolean).join(' — ')}</option>)}
                </Select>
                <Button
                  variant="accent"
                  disabled={!studyId}
                  onClick={() => { presetPeople([p.id]); navigate(`${studyPath(studyId)}/inviter`); }}
                >
                  Inviter
                </Button>
              </span>
            ) : null}
            <EditAction title={`Modifier — ${personName(p)}`}>
              {(close) => (
                <PersonForm
                  projectId={projectId}
                  existing={{ ...(row ?? p), ...p }}
                  pools={people.data?.pools ?? []}
                  roles={people.data?.roles ?? []}
                  onDone={close}
                  onErased={() => { close(); navigate(back); }}
                />
              )}
            </EditAction>
          </div>
        ) : null}
      </div>

      <dl className="ppl-stats">
        <div><dt>Interactions</dt><dd>{d.stats.interactions}</dd></div>
        <div><dt>Études</dt><dd>{d.stats.studies}</dd></div>
        <div><dt>Séances réalisées</dt><dd>{d.stats.sessions}</dd></div>
        <div><dt>Verbatims</dt><dd>{d.stats.verbatims}</dd></div>
      </dl>

      <div className="ppl-columns">
        <div className="ppl-col">
          <section className="test-block">
            <h2 className="t-h3">📅 Agenda</h2>
            {d.agenda.length === 0 ? (
              <p className="card-note">Aucun rendez-vous à venir.</p>
            ) : (
              <ul className="ppl-agenda">
                {d.agenda.map((a) => (
                  <li key={a.invitationId}>
                    <span><strong>{when(a.startsAt)}</strong> · {a.durationMin} min</span>
                    <span className="inv-sub">
                      {a.study ? <Link to={studyPath(a.studyId)} className="card-title-link">{a.study}</Link> : null}
                      {' · '}{a.mode === 'onsite' ? `sur place${a.location ? ` (${a.location})` : ''}` : a.provider ? PROVIDER_LABELS[a.provider] : 'visio'}
                      {a.organizer ? ` · avec ${a.organizer}` : ''}
                    </span>
                    <span className="inv-row-actions">
                      {a.meetingUrl ? <a href={a.meetingUrl} target="_blank" rel="noreferrer noopener" className="card-title-link">Rejoindre</a> : null}
                      <a
                        className="card-title-link"
                        href={googleCalendarLink({ title: `Entretien — ${personName(p)}`, startsAt: a.startsAt, durationMin: a.durationMin, details: a.meetingUrl ?? a.location ?? '' })}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        Ajouter à Google Agenda
                      </a>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="test-block">
            <h2 className="t-h3">🧭 Historique</h2>
            {d.timeline.length === 0 ? (
              <p className="card-note">Aucune interaction pour l’instant : elle n’a encore été invitée nulle part.</p>
            ) : (
              <ol className="ppl-timeline">
                {d.timeline.map((e, i) => (
                  <li key={`${e.kind}-${e.at}-${i}`}>
                    <span className="ppl-tl-icon" aria-hidden="true">{ICONS[e.kind]}</span>
                    <span>
                      <span className="ppl-tl-label">{e.label}</span>
                      {e.detail ? <span className="inv-sub">« {e.detail} »</span> : null}
                      <span className="inv-sub">
                        {day(e.at)}
                        {e.study ? <> · <Link to={studyPath(e.studyId)} className="card-title-link">{e.study}</Link></> : null}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>

        <div className="ppl-col">
          <section className="test-block">
            <h2 className="t-h3">🔬 Études</h2>
            {d.studies.length === 0 ? (
              <p className="card-note">Aucune étude pour l’instant.</p>
            ) : (
              <ul className="ppl-studies">
                {d.studies.map((s) => (
                  <li key={s.id}>
                    <Link to={studyPath(s.id)} className="card-title-link">{s.label ?? s.id}</Link>
                    <span className="inv-sub">
                      {s.invitations} invitation{s.invitations > 1 ? 's' : ''} · {s.interviews} entretien{s.interviews > 1 ? 's' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="test-block">
            <h2 className="t-h3">✉️ Invitations</h2>
            {d.invitations.length === 0 ? (
              <p className="card-note">Jamais invitée.</p>
            ) : (
              <ul className="ppl-studies">
                {d.invitations.map((i) => {
                  const b = BOOKING_LABELS[i.booking as BookingStatus] ?? BOOKING_LABELS.invite;
                  return (
                    <li key={i.id}>
                      <span><Chip state={chipState(b.tone)}>{b.label}</Chip> {i.study}</span>
                      <span className="inv-sub">
                        {i.startsAt ? when(i.startsAt) : `${i.slots} créneau${i.slots > 1 ? 'x' : ''} proposé${i.slots > 1 ? 's' : ''}`}
                        {i.organizer ? ` · par ${i.organizer}` : ''} · envoyée le {day(i.createdAt)}
                      </span>
                      {i.reason ? <span className="inv-sub">« {i.reason} »</span> : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="test-block">
            <h2 className="t-h3">Fiche</h2>
            <dl className="ppl-facts">
              {facts.filter(([, v]) => v).map(([k, v]) => (
                <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
              ))}
            </dl>
          </section>
        </div>
      </div>
      {d.timeline.length === 0 && d.agenda.length === 0 && !d.canWrite ? (
        <EmptyState title="Rien à afficher" description="Cette personne n’a pas encore participé à une étude." />
      ) : null}
    </main>
  );
}
